import { API_URL } from '../config';

class ActivityTracker {
  constructor() {
    // Load tracking state from localStorage, default to true
    this.isTracking = this.loadTrackingState();
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();
    this.pageViews = 0;
    this.apiCalls = 0;
    this.trackingListeners = new Set();
    
    // Initialize tracking
    this.initializeTracking();
  }

  // Helper method to get auth token from localStorage
  getAuthToken() {
    try {
      return localStorage.getItem('auth_token');
    } catch (e) {
      return null;
    }
  }

  // Helper method to get auth headers
  getAuthHeaders() {
    const headers = {};
    const authToken = this.getAuthToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    return headers;
  }

  // Check if current user is admin
  isCurrentUserAdmin() {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        return user.user_type === 'admin';
      }
    } catch (error) {
      console.warn('Error checking admin status:', error);
    }
    return false;
  }

  // Load tracking state from localStorage
  loadTrackingState() {
    try {
      const saved = localStorage.getItem('activityTracking');
      return saved !== null ? JSON.parse(saved) : true;
    } catch (error) {
      console.warn('Error loading tracking state:', error);
      return true;
    }
  }

  // Save tracking state to localStorage
  saveTrackingState() {
    try {
      localStorage.setItem('activityTracking', JSON.stringify(this.isTracking));
    } catch (error) {
      console.warn('Error saving tracking state:', error);
    }
  }

  initializeTracking() {
    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.updateLastActivity();
      }
    });

    // Track user interactions
    ['click', 'scroll', 'keypress', 'mousemove'].forEach(event => {
      document.addEventListener(event, this.throttle(() => {
        this.updateLastActivity();
      }, 30000)); // Throttle to once every 30 seconds
    });

    // Track page unload
    window.addEventListener('beforeunload', () => {
      if (this.isTracking && !this.isCurrentUserAdmin()) {
        this.trackActivity('session_end', {
          session_duration: Date.now() - this.sessionStartTime,
          page_views: this.pageViews,
          api_calls: this.apiCalls
        });
      }
    });
  }

  updateLastActivity() {
    if (this.isTracking && !this.isCurrentUserAdmin()) {
      this.lastActivityTime = Date.now();
    }
  }

  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }

  async trackActivity(activityType, details = null) {
    // Skip tracking for admin users
    if (this.isCurrentUserAdmin()) {
      return;
    }

    if (!this.isTracking) {
      return;
    }

    try {
      //check for the user is logged in or not 
      const user = localStorage.getItem('user');
      if (!user) {
        return;
      }
      const formData = new FormData();
      formData.append('activity_type', activityType);
      
      if (details) {
        formData.append('details', JSON.stringify(details));
      }

      const headers = this.getAuthHeaders();
      
      const response = await fetch(`${API_URL}/activity/track`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: headers
      });

      if (response.ok) {
        this.apiCalls++;
        return await response.json();
      }
    } catch (error) {
      console.error('Error tracking activity:', error);
    }
  }

  trackPageView(pageName) {
   
    if (this.isCurrentUserAdmin()) {
      return;
    }
    if (!this.isTracking) {
      return;
    }
    
    this.pageViews++;
    // console.log('ActivityTracker: Incremented pageViews to:', this.pageViews);
    this.trackActivityWithPageName('page_view', pageName);
  }

  async trackActivityWithPageName(activityType, pageName, details = null) {
    // console.log('ActivityTracker: trackActivityWithPageName called with:', { activityType, pageName, details });
    
    // Skip tracking for admin users
    if (this.isCurrentUserAdmin()) {
      return;
    }

    if (!this.isTracking) {
      return;
    }

    try {
      //check for the user is logged in or not 
      const user = localStorage.getItem('user');
      if (!user) {
        return;
      }
      const formData = new FormData();
      formData.append('activity_type', activityType);
      formData.append('page_name', pageName);
      
      if (details) {
        formData.append('details', JSON.stringify(details));
      }

      const headers = this.getAuthHeaders();
      
      const response = await fetch(`${API_URL}/activity/track`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: headers
      });

      if (response.ok) {
        this.apiCalls++;
        const result = await response.json();
        return result;
      } else {
        const errorText = await response.text();
        console.error('ActivityTracker: Error response:', errorText);
      }
    } catch (error) {
      console.error('ActivityTracker: Error tracking activity:', error);
    }
  }

  trackLogin(method = 'email') {
    if (this.isCurrentUserAdmin()) return;
    if (!this.isTracking) return;
    this.trackActivity('login', { login_method: method });
  }

  trackWorkoutPlanGeneration(planDetails) {
    if (this.isCurrentUserAdmin()) return;
    if (!this.isTracking) return;
    this.trackActivity('workout_plan', planDetails);
  }

  trackDietPlanGeneration(planDetails) {
    if (this.isCurrentUserAdmin()) return;
    if (!this.isTracking) return;
    this.trackActivity('diet_plan', planDetails);
  }

  trackExerciseSession(exerciseDetails) {
    if (this.isCurrentUserAdmin()) return;
    if (!this.isTracking) return;
    this.trackActivity('exercise', exerciseDetails);
  }

  trackAITrainerUsage(trainerDetails) {
    if (this.isCurrentUserAdmin()) return;
    if (!this.isTracking) return;
    this.trackActivity('ai_trainer', trainerDetails);
  }

  trackProfileView() {
    if (this.isCurrentUserAdmin()) return;
    if (!this.isTracking) return;
    this.trackActivity('profile_view');
  }

  trackProfileUpdate(updatedFields) {
    if (this.isCurrentUserAdmin()) return;
    if (!this.isTracking) return;
    this.trackActivity('profile_update', { updated_fields: updatedFields });
  }

  trackFeedbackSubmission(feedbackDetails) {
    if (this.isCurrentUserAdmin()) return;
    if (!this.isTracking) return;
    this.trackActivity('feedback', feedbackDetails);
  }

  // Get user engagement metrics
  async getUserEngagementMetrics(period = 'week') {
    try {
      const headers = this.getAuthHeaders();
      
      const response = await fetch(`${API_URL}/activity/engagement-metrics?period=${period}`, {
        method: 'GET',
        credentials: 'include',
        headers: headers
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Error fetching engagement metrics:', error);
    }
    return null;
  }

  // Get user activity summary
  async getUserActivitySummary(days = 30) {
    try {
      const headers = this.getAuthHeaders();
      
      const response = await fetch(`${API_URL}/activity/my-activity?days=${days}`, {
        method: 'GET',
        credentials: 'include',
        headers: headers
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Error fetching activity summary:', error);
    }
    return null;
  }

  // Get daily activity stats
  async getDailyActivityStats(startDate = null, endDate = null) {
    try {
      //check for the user is logged in or not 
      const user = localStorage.getItem('user');
      if (!user) {
        return;
      }
      let url = `${API_URL}/activity/daily-stats`;
      const params = new URLSearchParams();
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const headers = this.getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: headers
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Error fetching daily activity stats:', error);
    }
    return null;
  }

  // Enhanced tracking control methods
  setTracking(enabled) {
    const wasTracking = this.isTracking;
    this.isTracking = enabled;
    this.saveTrackingState();
    
    // Notify listeners of state change
    this.notifyTrackingListeners(enabled, wasTracking);
    
    // Log the change
    
    // Track the toggle event itself (if enabling tracking and not admin)
    if (enabled && !wasTracking && !this.isCurrentUserAdmin()) {
      this.trackActivity('tracking_enabled', { 
        timestamp: new Date().toISOString(),
        session_duration_when_enabled: Date.now() - this.sessionStartTime
      });
    } else if (!enabled && wasTracking && !this.isCurrentUserAdmin()) {
      // Track before disabling
      this.trackActivity('tracking_disabled', { 
        timestamp: new Date().toISOString(),
        session_duration_when_disabled: Date.now() - this.sessionStartTime,
        page_views_when_disabled: this.pageViews,
        api_calls_when_disabled: this.apiCalls
      });
    }
  }

  // Enable tracking
  enableTracking() {
    this.setTracking(true);
  }

  // Disable tracking
  disableTracking() {
    this.setTracking(false);
  }

  // Toggle tracking state
  toggleTracking() {
    this.setTracking(!this.isTracking);
    return this.isTracking;
  }

  // Check if tracking is enabled (also considers admin status)
  isTrackingEnabled() {
    return this.isTracking && !this.isCurrentUserAdmin();
  }

  // Add listener for tracking state changes
  addTrackingListener(callback) {
    this.trackingListeners.add(callback);
    return () => this.trackingListeners.delete(callback);
  }

  // Notify all listeners of tracking state changes
  notifyTrackingListeners(enabled, wasTracking) {
    this.trackingListeners.forEach(callback => {
      try {
        callback(enabled, wasTracking);
      } catch (error) {
        console.error('Error in tracking listener:', error);
      }
    });
  }

  // Get current session info with tracking status
  getSessionInfo() {
    return {
      sessionDuration: Date.now() - this.sessionStartTime,
      pageViews: this.pageViews,
      apiCalls: this.apiCalls,
      lastActivity: this.lastActivityTime,
      isTracking: this.isTracking,
      isAdmin: this.isCurrentUserAdmin(),
      effectiveTracking: this.isTracking && !this.isCurrentUserAdmin()
    };
  }

  // Reset session counters (useful when tracking is re-enabled)
  resetSession() {
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();
    this.pageViews = 0;
    this.apiCalls = 0;
  }

  // Get tracking statistics
  getTrackingStats() {
    return {
      isEnabled: this.isTracking,
      isAdmin: this.isCurrentUserAdmin(),
      effectiveTracking: this.isTracking && !this.isCurrentUserAdmin(),
      sessionDuration: Date.now() - this.sessionStartTime,
      pageViews: this.pageViews,
      apiCalls: this.apiCalls,
      lastActivity: this.lastActivityTime,
      sessionStartTime: this.sessionStartTime
    };
  }
}

// Create a singleton instance
const activityTracker = new ActivityTracker();

export default activityTracker; 